import { useCallback, useState } from "react";

import { useSearchParams } from "react-router";

import {
  QueryPaginatedRoleplaysOrderBy,
  usePaginatedRoleplaysQuery,
  VisibilityScope,
} from "@/graphql";

const FETCH_LIMIT = 10;

export const useRoleplayListQuery = ({
  includeOwnerData,
  skip = false,
  pageSize = FETCH_LIMIT,
  workspaceOnly = false,
}: {
  includeOwnerData?: boolean;
  skip?: boolean;
  pageSize?: number;
  workspaceOnly?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract values from searchParams
  const searchQuery = searchParams.get("query") || null;
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const selectedOwner = searchParams.get("owner") || null;

  // Parse selectedScopes from searchParams
  const scopesParam = searchParams.get("scopes");
  const selectedScopes = scopesParam
    ? (scopesParam.split(",") as VisibilityScope[])
    : [];

  // Parse order from searchParams
  const orderParam = searchParams.get("order");
  const order =
    (orderParam as QueryPaginatedRoleplaysOrderBy) ||
    QueryPaginatedRoleplaysOrderBy.Default;

  // Parse selectedTags from searchParams
  const tagsParam = searchParams.get("tags");
  const selectedTags = tagsParam ? tagsParam.split(",") : [];

  // Add error state back
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    loading: loadingData,
    refetch: refetchRoleplays,
  } = usePaginatedRoleplaysQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      currentPage: currentPage,
      pageSize: pageSize,
      query: searchQuery,
      scopes: selectedScopes,
      ownerIds: selectedOwner ? [selectedOwner] : null,
      workspaceOnly: workspaceOnly,
      tagIds: selectedTags,
      includeOwnerData: includeOwnerData,
      orderBy: order,
    },
    skip,
    onError: () => {
      setError("Error fetching roleplays");
    },
  });

  // Setter functions that update searchParams
  const setSearchQuery = useCallback(
    (query: string | null) => {
      const newParams = new URLSearchParams(searchParams);
      if (query) {
        newParams.set("query", query);
      } else {
        newParams.delete("query");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setCurrentPage = useCallback(
    (page: number) => {
      const newParams = new URLSearchParams(searchParams);
      if (page === 1) {
        newParams.delete("page");
      } else {
        newParams.set("page", page.toString());
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedOwner = useCallback(
    (owner: string | null) => {
      const newParams = new URLSearchParams(searchParams);
      if (owner) {
        newParams.set("owner", owner);
      } else {
        newParams.delete("owner");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedScopes = useCallback(
    (scopes: VisibilityScope[]) => {
      const newParams = new URLSearchParams(searchParams);
      if (scopes.length > 0) {
        newParams.set("scopes", scopes.join(","));
      } else {
        newParams.delete("scopes");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setOrder = useCallback(
    (newOrder: QueryPaginatedRoleplaysOrderBy) => {
      const newParams = new URLSearchParams(searchParams);
      if (newOrder !== QueryPaginatedRoleplaysOrderBy.Default) {
        newParams.set("order", newOrder);
      } else {
        newParams.delete("order");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedTags = useCallback(
    (tags: string[]) => {
      const newParams = new URLSearchParams(searchParams);
      if (tags.length > 0) {
        newParams.set("tags", tags.join(","));
      } else {
        newParams.delete("tags");
      }
      newParams.delete("page"); // Reset to page 1
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const totalRecords = data?.paginatedRoleplays?.totalRecords || 0;
  return {
    roleplays: data?.paginatedRoleplays?.data || [],
    totalRecords,
    loading: loadingData,
    error,
    refetchRoleplays,
    setSelectedOwner,
    setSelectedTags,
    setOrder,
    setSelectedScopes,
    setCurrentPage,
    setSearchQuery,
    selectedScopes,
    selectedOwner,
    selectedTags,
    order,
    currentPage,
    searchQuery,
  };
};
